/**
 * Utility functions for smart file/folder naming
 * Handles duplicate detection and unique name generation
 */

/**
 * Generate a unique name by adding (Copy), (2), (3), etc.
 * @param baseName - The base name without extension
 * @param extension - File extension (optional, for files only)
 * @param existingNames - Array of existing names to check against
 * @returns A unique name that doesn't conflict with existing names
 */
export const generateUniqueName = (
  baseName: string,
  extension: string | undefined,
  existingNames: string[]
): string => {
  const lowerExistingNames = existingNames.map(name => name.toLowerCase());

  // Try "name (Copy)" first
  let suggestion = `${baseName} (Copy)`;
  let fullName = extension ? `${suggestion}.${extension}` : suggestion;
  
  if (!lowerExistingNames.includes(fullName.toLowerCase())) {
    return fullName;
  }

  // Try "name (2)", "name (3)", etc.
  let counter = 2;
  while (counter < 100) {
    suggestion = `${baseName} (${counter})`;
    fullName = extension ? `${suggestion}.${extension}` : suggestion;
    
    if (!lowerExistingNames.includes(fullName.toLowerCase())) {
      return fullName;
    }
    counter++;
  }

  // Fallback with timestamp
  const timestamp = Date.now();
  suggestion = `${baseName} (${timestamp})`;
  return extension ? `${suggestion}.${extension}` : suggestion;
};

/**
 * Check if a name already exists in a list (case-insensitive)
 * @param name - Name to check
 * @param existingNames - Array of existing names
 * @returns true if name exists, false otherwise
 */
export const isDuplicateName = (name: string, existingNames: string[]): boolean => {
  const lowerName = name.toLowerCase();
  return existingNames.some(existing => existing.toLowerCase() === lowerName);
};

/**
 * Extract base name and extension from a filename
 * @param filename - Full filename with extension
 * @returns Object with baseName and extension
 */
export const splitFilename = (filename: string): { baseName: string; extension: string | undefined } => {
  const lastDotIndex = filename.lastIndexOf('.');
  
  if (lastDotIndex === -1 || lastDotIndex === 0) {
    // No extension or hidden file
    return { baseName: filename, extension: undefined };
  }
  
  const baseName = filename.substring(0, lastDotIndex);
  const extension = filename.substring(lastDotIndex + 1);
  
  return { baseName, extension };
};

/**
 * Get unique name for a file in a specific folder
 * @param filename - Original filename
 * @param folderId - Target folder ID
 * @param files - Array of all files
 * @returns Unique filename
 */
export const getUniqueFilename = (
  filename: string,
  folderId: string | null,
  files: any[]
): string => {
  // Get existing file names in the target folder
  const existingNames = files
    .filter((f: any) => f.folder_id === folderId && !f.is_deleted)
    .map((f: any) => f.name);

  // Check if name already exists
  if (!isDuplicateName(filename, existingNames)) {
    return filename;
  }

  // Generate unique name
  const { baseName, extension } = splitFilename(filename);
  return generateUniqueName(baseName, extension, existingNames);
};

/**
 * Get unique name for a folder in a specific parent folder
 * @param folderName - Original folder name
 * @param parentId - Parent folder ID
 * @param folders - Array of all folders
 * @returns Unique folder name
 */
export const getUniqueFolderName = (
  folderName: string,
  parentId: string | null,
  folders: any[]
): string => {
  // Get existing folder names in the target parent
  const existingNames = folders
    .filter((f: any) => f.parent_id === parentId && !f.is_deleted)
    .map((f: any) => f.name);

  // Check if name already exists
  if (!isDuplicateName(folderName, existingNames)) {
    return folderName;
  }

  // Generate unique name
  return generateUniqueName(folderName, undefined, existingNames);
};
/**
 * Check if a filename already includes its extension
 * @param filename - The filename to check
 * @param extension - The extension to check for
 * @returns true if filename already ends with the extension
 */
export const filenameIncludesExtension = (filename: string, extension: string): boolean => {
  if (!filename || !extension) return false;
  return filename.toLowerCase().endsWith(`.${extension.toLowerCase()}`);
};

/**
 * Get display name with extension if needed
 * @param filename - The base filename
 * @param extension - The file extension
 * @returns Object with displayName and shouldShowExtension flag
 */
export const getFileDisplayInfo = (filename: string, extension?: string): {
  displayName: string;
  shouldShowExtension: boolean;
} => {
  if (!extension) {
    return { displayName: filename, shouldShowExtension: false };
  }
  
  const alreadyHasExtension = filenameIncludesExtension(filename, extension);
  return {
    displayName: filename,
    shouldShowExtension: !alreadyHasExtension
  };
};